import {useCodeViewAddBanner} from '../contexts/CodeViewBannersContext'
import {usePath} from '../contexts/PathContext'
import {useRefInfo} from '../contexts/RefInfoContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryTreePath} from '@github-ui/paths'
import type {Commit} from '@github-ui/repos-types'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useEffect, useState} from 'react'

interface CommitInfoState {
  commitInfo?: Record<string, Commit>
  loading?: boolean
  error?: boolean
}

export function useCommitInfo(): CommitInfoState {
  const path = usePath()
  const refInfo = useRefInfo()
  const repo = useCurrentRepository()
  const [state, setState] = useState<CommitInfoState>({loading: true})
  const addBanner = useCodeViewAddBanner()

  // Use stable primitives rather than the repo object or a derived string so this
  // effect doesn't re-fire when repo object identity changes (e.g. context re-create).
  const repoName = repo.name
  const repoOwner = repo.ownerLogin
  const refName = refInfo.name

  useEffect(() => {
    const commitPath = repositoryTreePath({
      repo: {name: repoName, ownerLogin: repoOwner},
      action: 'tree-commit-info',
      commitish: refName,
      path,
    })
    let cancelled = false
    const update = async () => {
      setState({loading: true})
      const response = await verifiedFetchJSON(commitPath)

      if (cancelled) {
        return
      }

      try {
        if (response.ok) {
          setState({commitInfo: await response.json()})
        } else {
          addBanner({variant: 'warning', message: 'Failed to load latest commit information.'})
          setState({error: true})
        }
      } catch {
        setState({error: true})
      }
    }

    update()

    // This method will be called if commitPath changes, in order to ignore the old result
    return function cancel() {
      cancelled = true
    }
  }, [addBanner, path, refName, repoName, repoOwner])

  return state
}
